import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Menu, X } from "lucide-react";
import { useAuth } from "@/lib/AuthContext";
import { Button } from "@/components/ui/button";
import CountryLanguageSelector from "@/components/CountryLanguageSelector";

const NAV_ITEMS = [
    { to: "/", key: "home" },
    { to: "/browse", key: "deals" },
    { to: "/pricing", key: "membership" },
    { to: "/faq", key: "faq" },
];

// Footer link groups — hoisted outside the component so React doesn't
// recreate them on every render (each FooterCol receives a stable
// reference and can bail out of re-rendering).
const FOOTER_EXPLORE = [
    ["Browse deals", "/browse"],
    ["Membership plans", "/pricing"],
    ["Frequently asked", "/faq"],
];
const FOOTER_VENDORS = [
    ["Become a partner", "/become-a-vendor"],
    ["Vendor login", "/login"],
];
const FOOTER_CREATORS = [
    ["Apply as an affiliate", "/affiliates/apply"],
    ["Affiliate login", "/login"],
];
const FOOTER_COMPANY = [
    ["About", "#"],
    ["Careers", "#"],
    ["Panda Promise", "#"],
    ["Legal", "#"],
];

export default function PublicLayout() {
    const { user, logout } = useAuth();
    const { t } = useTranslation();
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const location = useLocation();

    useEffect(() => setOpen(false), [location.pathname]);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 8);
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const dashHome =
        user && user.role === "admin"
            ? "/admin"
            : user && user.role === "broker"
              ? "/broker"
              : "/member";

    return (
        <div className="min-h-screen bg-cream">
            <header
                data-testid="public-header"
                className={`sticky top-0 z-40 border-b transition-colors ${
                    scrolled
                        ? "bg-white/90 backdrop-blur-xl border-gray-100 shadow-[0_1px_0_rgba(0,0,0,0.04)]"
                        : "bg-white border-transparent"
                }`}
            >
                <div className="mx-auto max-w-7xl px-6 lg:px-10 h-20 flex items-center justify-between">
                    <Link
                        to="/"
                        data-testid="brand-link"
                        className="flex items-center gap-3"
                    >
                        <img
                            src="/perx_logo.png"
                            alt="Perx Group"
                            className="h-9 w-9 object-contain"
                        />
                        <div className="leading-none">
                            <div className="font-display text-xl text-navy-700">
                                Perx Group
                            </div>
                            <div className="text-[10px] tracking-[0.25em] text-navy-500 uppercase">
                                Members Club
                            </div>
                        </div>
                    </Link>

                    <nav className="hidden lg:flex items-center gap-10">
                        {NAV_ITEMS.map((n) => (
                            <NavLink
                                key={n.to}
                                to={n.to}
                                end={n.to === "/"}
                                data-testid={`nav-${n.key}`}
                                className={({ isActive }) =>
                                    `text-sm font-medium tracking-wide transition-colors ${
                                        isActive
                                            ? "text-navy-700"
                                            : "text-navy-500 hover:text-navy-700"
                                    }`
                                }
                            >
                                {t(`nav.${n.key}`)}
                            </NavLink>
                        ))}
                    </nav>

                    <div className="hidden lg:flex items-center gap-3">
                        <CountryLanguageSelector />
                        {user ? (
                            <>
                                <Link to={dashHome} data-testid="header-dashboard">
                                    <Button
                                        variant="outline"
                                        className="rounded-full border-navy-700 text-navy-700 hover:bg-navy-700 hover:text-white"
                                    >
                                        {t("nav.dashboard")}
                                    </Button>
                                </Link>
                                <Button
                                    data-testid="header-logout"
                                    onClick={logout}
                                    className="rounded-full bg-navy-700 hover:bg-navy-800 text-white"
                                >
                                    {t("nav.logout")}
                                </Button>
                            </>
                        ) : (
                            <>
                                <Link to="/login" data-testid="header-login">
                                    <Button
                                        variant="ghost"
                                        className="text-navy-700 hover:bg-navy-50"
                                    >
                                        {t("nav.login")}
                                    </Button>
                                </Link>
                                <Link to="/signup" data-testid="header-signup">
                                    <Button className="rounded-full bg-gold-500 hover:bg-gold-600 text-navy-900 font-semibold px-5">
                                        {t("nav.join")}
                                    </Button>
                                </Link>
                            </>
                        )}
                    </div>

                    <button
                        data-testid="mobile-menu-toggle"
                        onClick={() => setOpen((o) => !o)}
                        className="lg:hidden p-2 text-navy-700"
                        aria-label="Toggle menu"
                    >
                        {open ? <X size={22} /> : <Menu size={22} />}
                    </button>
                </div>

                {open && (
                    <div className="lg:hidden border-t border-gray-100 bg-white px-6 py-5 space-y-4">
                        <div className="pb-3 border-b border-gray-100">
                            <CountryLanguageSelector />
                        </div>
                        {NAV_ITEMS.map((n) => (
                            <NavLink
                                key={n.to}
                                to={n.to}
                                end={n.to === "/"}
                                className="block text-navy-700 font-medium"
                            >
                                {t(`nav.${n.key}`)}
                            </NavLink>
                        ))}
                        {user ? (
                            <>
                                <Link to={dashHome} className="block text-navy-700 font-medium">
                                    {t("nav.dashboard")}
                                </Link>
                                <button
                                    onClick={logout}
                                    className="block text-navy-700 font-medium"
                                >
                                    {t("nav.logout")}
                                </button>
                            </>
                        ) : (
                            <>
                                <Link to="/login" className="block text-navy-700 font-medium">
                                    {t("nav.login")}
                                </Link>
                                <Link
                                    to="/signup"
                                    className="block text-gold-600 font-semibold"
                                >
                                    {t("nav.join")} →
                                </Link>
                            </>
                        )}
                    </div>
                )}
            </header>

            <main>
                <Outlet />
            </main>

            <PublicFooter />
        </div>
    );
}

function PublicFooter() {
    return (
        <footer
            data-testid="public-footer"
            className="mt-24 bg-navy-700 text-navy-50"
        >
            <div className="mx-auto max-w-7xl px-6 lg:px-10 py-16">
                <div className="grid gap-12 lg:grid-cols-4">
                    <div>
                        <div className="flex items-center gap-3 mb-5">
                            <img
                                src="/perx_logo_golden.png"
                                alt="Perx"
                                className="h-9 w-9 object-contain"
                            />
                            <div className="font-display text-2xl text-white">
                                Perx Group
                            </div>
                        </div>
                        <p className="text-sm text-navy-50/70 max-w-xs leading-relaxed">
                            A quiet, thoughtful members club that negotiates the good stuff
                            on your behalf. Powered by pandas.
                        </p>
                    </div>
                    <FooterCol title="Explore" links={FOOTER_EXPLORE} />
                    <FooterCol title="For vendors" links={FOOTER_VENDORS} />
                    <FooterCol title="For creators" links={FOOTER_CREATORS} />
                    <FooterCol title="Company" links={FOOTER_COMPANY} />
                </div>
                <div className="mt-14 pt-8 border-t border-white/10 text-xs text-navy-50/50 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                    <div>© {new Date().getFullYear()} Perx Group. All perks reserved.</div>
                    <div className="tracking-[0.3em] uppercase">
                        Made with bamboo · Not with cookies
                    </div>
                </div>
            </div>
        </footer>
    );
}

function FooterCol({ title, links }) {
    return (
        <div>
            <div className="text-xs uppercase tracking-[0.25em] text-gold-500 mb-4">
                {title}
            </div>
            <ul className="space-y-3 text-sm text-navy-50/80">
                {links.map(([l, to]) => (
                    <li key={l}>
                        <Link to={to} className="hover:text-white transition-colors">
                            {l}
                        </Link>
                    </li>
                ))}
            </ul>
        </div>
    );
}
